import { c as _c } from "react/compiler-runtime";
import { BulkChangeGroupRole } from "@components/table/members/bulk-actions/bulk-change-group-role";
import { BulkExportMembers } from "@components/table/members/bulk-actions/bulk-export-members";
import { BulkRemoveMember } from "@components/table/members/bulk-actions/bulk-remove-member";
import { BulkResendInvitation } from "@components/table/members/bulk-actions/bulk-resend-invitation";
import { BulkSendEmail } from "@components/table/members/bulk-actions/bulk-send-email";
import { useHasUserValidRoleGroupLevel } from "@hooks/access-control/use-has-user-valid-role-group-level";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useOnGroupRemoved } from "@hooks/use-on-group-removed";
import { useShouldShowBulkAction } from "@hooks/table/use-should-show-bulk-action";
import { useAppSelector } from "@store/store-helper";
import { fetchingGroupsFlagsSelector } from "@store/groups/groups-selector";
import { selectedRowIdsSelector } from "@store/table/table-selector";
import { useMemo } from "react";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
/** List all the bulk action components for group member table */
export function useGroupMemberBulkActions(t0) {
  const $ = _c(57);
  const {
    companyId,
    group,
    members
  } = t0;
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  const {
    isFetchingGroups
  } = useAppSelector(fetchingGroupsFlagsSelector);
  const t1 = group ?? null;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      selectedGroup: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    canEditMember,
    canDeleteMemberFromGroup,
    canResendInvitationToGroup
  } = useHasUserValidRoleGroupLevel(t2);
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const groupName = group?.name ?? "";
  const groupId = group?.id ?? "";
  let t3;
  if ($[2] !== companyId || $[3] !== groupName) {
    t3 = {
      companyId,
      groupName
    };
    $[2] = companyId;
    $[3] = groupName;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const onGroupRemoved = useOnGroupRemoved(t3);
  let filteredGroupMembers;
  let isAnyMemberPending;
  let isAnyRowSelected;
  let t4;
  if ($[5] !== members || $[6] !== selectedRowIds) {
    let t5;
    if ($[11] !== selectedRowIds) {
      t5 = member => selectedRowIds.some(identity => identity === member.identity);
      $[11] = selectedRowIds;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    filteredGroupMembers = members.filter(t5);
    isAnyRowSelected = filteredGroupMembers.length !== 0;
    isAnyMemberPending = filteredGroupMembers.some(_temp);
    t4 = filteredGroupMembers.some(_temp2);
    $[5] = members;
    $[6] = selectedRowIds;
    $[7] = filteredGroupMembers;
    $[8] = isAnyMemberPending;
    $[9] = isAnyRowSelected;
    $[10] = t4;
  } else {
    filteredGroupMembers = $[7];
    isAnyMemberPending = $[8];
    isAnyRowSelected = $[9];
    t4 = $[10];
  }
  const isAnyMemberTeam = t4;
  let allowedButtons;
  if ($[13] !== canAssignManagers || $[14] !== canDeleteMemberFromGroup || $[15] !== canEditMember || $[16] !== canResendInvitationToGroup || $[17] !== group || $[18] !== isAnyMemberPending || $[19] !== isAnyMemberTeam || $[20] !== isAnyRowSelected) {
    allowedButtons = ["export"];
    if (isAnyRowSelected && group) {
      if (!isAnyMemberTeam) {
        allowedButtons.push("sendEmail");
      }
      if (canEditMember && canAssignManagers) {
        allowedButtons.push("changeRole");
      }
      if (isAnyMemberPending && canResendInvitationToGroup) {
        allowedButtons.push("resendInvitation");
      }
      if (canDeleteMemberFromGroup) {
        allowedButtons.push("removeMember");
      }
    }
    $[13] = canAssignManagers;
    $[14] = canDeleteMemberFromGroup;
    $[15] = canEditMember;
    $[16] = canResendInvitationToGroup;
    $[17] = group;
    $[18] = isAnyMemberPending;
    $[19] = isAnyMemberTeam;
    $[20] = isAnyRowSelected;
    $[21] = allowedButtons;
  } else {
    allowedButtons = $[21];
  }
  const allowedButtonsBasedOnRowSelection = allowedButtons;
  let t5;
  if ($[22] !== allowedButtonsBasedOnRowSelection) {
    t5 = {
      allowedButtonsBasedOnRowSelection
    };
    $[22] = allowedButtonsBasedOnRowSelection;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  const {
    shouldShowBulkButtons
  } = useShouldShowBulkAction(t5);
  let t6;
  if ($[24] !== isFetchingGroups || $[25] !== members.length || $[26] !== shouldShowBulkButtons) {
    t6 = shouldShowBulkButtons("export") && (isFetchingGroups || members.length > 0);
    $[24] = isFetchingGroups;
    $[25] = members.length;
    $[26] = shouldShowBulkButtons;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const shouldShowExportButton = t6;
  if (!members.length) {
    let t7;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        changeRole: null,
        seats: null,
        addToTeam: null,
        resendInvitation: null,
        removeMember: null,
        sendEmail: null,
        export: null
      };
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    return t7;
  }
  let t7;
  if ($[29] !== companyId || $[30] !== groupId || $[31] !== shouldShowBulkButtons) {
    t7 = shouldShowBulkButtons("changeRole") ? <BulkChangeGroupRole companyId={companyId} groupId={groupId} /> : null;
    $[29] = companyId;
    $[30] = groupId;
    $[31] = shouldShowBulkButtons;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== companyId || $[34] !== filteredGroupMembers || $[35] !== shouldShowBulkButtons) {
    t8 = shouldShowBulkButtons("resendInvitation") ? <BulkResendInvitation selectedEntities={filteredGroupMembers} companyId={companyId} subjectType="group" /> : null;
    $[33] = companyId;
    $[34] = filteredGroupMembers;
    $[35] = shouldShowBulkButtons;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== companyId || $[38] !== groupId || $[39] !== onGroupRemoved || $[40] !== shouldShowBulkButtons) {
    t9 = shouldShowBulkButtons("removeMember") ? <BulkRemoveMember subjectType="group" companyId={companyId} groupId={groupId} onGroupRemoved={onGroupRemoved} /> : null;
    $[37] = companyId;
    $[38] = groupId;
    $[39] = onGroupRemoved;
    $[40] = shouldShowBulkButtons;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  let t10;
  if ($[42] !== filteredGroupMembers || $[43] !== shouldShowBulkButtons) {
    t10 = shouldShowBulkButtons("sendEmail") ? <BulkSendEmail selectedEntities={filteredGroupMembers} /> : null;
    $[42] = filteredGroupMembers;
    $[43] = shouldShowBulkButtons;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  let t11;
  if ($[45] !== filteredGroupMembers || $[46] !== groupName || $[47] !== isAnyRowSelected || $[48] !== members || $[49] !== shouldShowExportButton) {
    t11 = shouldShowExportButton ? <BulkExportMembers selectedEntities={isAnyRowSelected ? filteredGroupMembers : members} subjectType="group" entityName={groupName} areEntitiesSelected={isAnyRowSelected} /> : null;
    $[45] = filteredGroupMembers;
    $[46] = groupName;
    $[47] = isAnyRowSelected;
    $[48] = members;
    $[49] = shouldShowExportButton;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  let t12;
  if ($[51] !== t10 || $[52] !== t11 || $[53] !== t7 || $[54] !== t8 || $[55] !== t9) {
    t12 = {
      changeRole: t7,
      seats: null,
      addToTeam: null,
      resendInvitation: t8,
      removeMember: t9,
      sendEmail: t10,
      export: t11
    };
    $[51] = t10;
    $[52] = t11;
    $[53] = t7;
    $[54] = t8;
    $[55] = t9;
    $[56] = t12;
  } else {
    t12 = $[56];
  }
  return t12;
}
function _temp2(member_1) {
  return FaroCoreApiUtils.Member.isTeam(member_1);
}
function _temp(member_0) {
  return !FaroCoreApiUtils.Member.isMemberActive(member_0);
}